<script setup lang="ts">
import { computed } from 'vue'
import { Handle, Position } from '@vue-flow/core'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, Volume2, Monitor, Copy } from 'lucide-vue-next'
import SpotifyIcon from '@/components/icons/SpotifyIcon.vue'
import NodeFrame from './NodeFrame.vue'
import type { SpotifyNode } from '@/stores/spotifyNodes'

interface Props {
  node: SpotifyNode
  isPlaying?: boolean
  currentTime?: number
  duration?: number
  artworkUrl?: string
  isMobile?: boolean
  displays: Array<{ id: string; name: string }>
  defaultPlaybackTarget: string
}

const props = defineProps<Props>()

// Compute progress percentage for spectrogram animation offset
const playProgress = computed(() => {
  if (!props.duration || props.duration === 0) return 0
  return ((props.currentTime || 0) / props.duration) * 100
})

const emit = defineEmits<{
  play: [node: SpotifyNode]
  edit: [node: SpotifyNode]
  delete: [node: SpotifyNode]
  duplicate: [node: SpotifyNode]
  updateVolume: [nodeId: string, volume: number]
  updatePlaybackTarget: [nodeId: string, target: string]
}>()

// Expose Position enum values for template
const handlePositions = {
  left: Position.Left
}
</script>

<template>
  <!-- Source handle (left) - for connecting to scenes -->
  <Handle 
    type="source" 
    :position="handlePositions.left" 
    id="audio-out"
    class="audio-handle"
  />
  
  <div 
    class="spotify-node-wrapper"
    :class="{ 
      'spotify-node-mobile': isMobile,
      'spotify-node-playing': isPlaying
    }"
  >
    <NodeFrame 
      :id="`spotify-${node.id}`"
      variant="spotify"
      :is-active="isPlaying"
      :show-glow="true"
    >
      <!-- Spectrogram glow effect (when playing) -->
      <div v-if="isPlaying" class="spectrogram-container" :style="{ '--progress': playProgress }">
        <div class="spectrogram-bar" style="--delay: 0s; --height: 60%"></div>
        <div class="spectrogram-bar" style="--delay: 0.2s; --height: 100%"></div>
        <div class="spectrogram-bar" style="--delay: 0.4s; --height: 40%"></div>
        <div class="spectrogram-bar" style="--delay: 0.1s; --height: 80%"></div>
        <div class="spectrogram-bar" style="--delay: 0.3s; --height: 50%"></div>
        <div class="spectrogram-bar" style="--delay: 0.5s; --height: 70%"></div>
        <div class="spectrogram-bar" style="--delay: 0.15s; --height: 90%"></div>
        <div class="spectrogram-bar" style="--delay: 0.35s; --height: 45%"></div>
      </div>
      
      <!-- Content -->
      <div class="spotify-node-content" @click.stop="emit('play', node)">
        <div class="spotify-node-icon">
          <img 
            v-if="artworkUrl"
            :src="artworkUrl"
            class="spotify-artwork"
            alt="Artwork"
          />
          <SpotifyIcon v-else class="h-6 w-6 text-white" />
        </div>
        
        <div class="spotify-node-info">
          <span class="spotify-node-name">{{ node.name }}</span>
          <div class="spotify-node-type">Spotify</div>
        </div>
      </div>
      
      <!-- Menu button positioned at bottom right -->
      <div class="spotify-node-menu-container">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button
              variant="ghost"
              size="icon"
              class="spotify-node-menu"
              @click.stop
            >
              <MoreHorizontal class="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem @click.stop="emit('edit', node)">
              <Pencil class="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem @click.stop="emit('duplicate', node)">
              <Copy class="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel class="text-xs text-muted-foreground">Volume</DropdownMenuLabel>
                <div class="px-2 py-1.5" @click.stop>
                  <div class="flex items-center gap-2">
                    <Volume2 class="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <Slider
                      :model-value="[node.volume * 100]"
                      :max="100"
                      :step="1"
                      class="flex-1"
                      @update:model-value="(v) => v && emit('updateVolume', node.id, v[0] / 100)"
                    />
                    <span class="text-xs text-muted-foreground w-8">{{ Math.round(node.volume * 100) }}%</span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuLabel class="text-xs text-muted-foreground">Play on</DropdownMenuLabel>
                <DropdownMenuRadioGroup 
                  :model-value="node.playback_target || defaultPlaybackTarget"
                  @update:model-value="(v) => emit('updatePlaybackTarget', node.id, v)"
                >
                  <DropdownMenuRadioItem 
                    v-for="display in displays" 
                    :key="display.id" 
                    :value="display.id"
                    @click.stop
                  >
                    <Monitor class="h-4 w-4 mr-2" />
                    {{ display.name }}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  @click.stop="emit('delete', node)"
                  class="text-destructive focus:text-destructive"
                >
                  <Trash2 class="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
      </div>
    </NodeFrame>
  </div>
</template>

<style scoped>
.spotify-node-wrapper {
  width: 220px;
  height: 72px;
  cursor: default;
  transition: transform 200ms ease;
}

.spotify-node-mobile {
  width: 180px;
  height: 60px;
}

.spotify-node-wrapper:hover {
  transform: translateY(-2px);
}

/* Disable hover effects on mobile */
@media (hover: none) {
  .spotify-node-wrapper:hover {
    transform: none;
  }
}

/* Spectrogram effect */
.spectrogram-container {
  position: absolute;
  inset: -10px;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  padding: 0 16px;
  pointer-events: none;
  z-index: -1;
  filter: blur(8px);
  opacity: 0.5;
}

.spectrogram-bar {
  width: 8px;
  background: var(--gold-gradient-light);
  border-radius: 4px;
  animation: spectrogram-bounce 0.8s ease-in-out infinite;
  animation-delay: var(--delay, 0s);
  height: var(--height, 50%);
}

@keyframes spectrogram-bounce {
  0%, 100% {
    transform: scaleY(0.3);
    opacity: 0.5;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
}

/* Content */
.spotify-node-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  height: 100%;
  cursor: pointer;
}

.spotify-node-mobile .spotify-node-content {
  gap: 8px;
  padding: 8px 12px;
}

.spotify-node-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: #1DB954;
  flex-shrink: 0;
  overflow: hidden;
}

.spotify-node-mobile .spotify-node-icon {
  width: 36px;
  height: 36px;
}

.spotify-artwork {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.spotify-node-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.spotify-node-name {
  font-weight: 600;
  font-size: 14px;
  color: hsl(var(--foreground));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spotify-node-mobile .spotify-node-name {
  font-size: 12px;
}

.spotify-node-menu-container {
  position: absolute;
  bottom: 4px;
  right: 8px;
}

.spotify-node-menu {
  width: 24px;
  height: 24px;
  opacity: 0;
  transition: opacity 150ms ease;
}

.spotify-node-wrapper:hover .spotify-node-menu,
.spotify-node-menu:focus,
.spotify-node-menu[data-state="open"] {
  opacity: 1;
}

.spotify-node-type {
  font-size: 11px;
  color: hsl(var(--muted-foreground));
  margin-top: 2px;
}

/* Audio handle styling */
.audio-handle {
  background: var(--gold-solid) !important;
}

:deep(.vue-flow__handle.audio-handle) {
  width: 8px;
  height: 8px;
  background: var(--gold-solid);
}
</style>
